// AI provider store — browser-only persistence for the user's
// chosen LLM provider, API key, and model selection.
//
// Privacy posture (Req 22.5):
//   The Link server CANNOT see the user's prompts or responses
//   when the AI pane is talking to a provider. The key lives in
//   the browser's IndexedDB next to the Identity_Key, and we hit
//   the provider directly with `fetch`. Nothing here ever crosses
//   our backend.
//
// IndexedDB layout
// ----------------
// Database `link.local` / object store `prefs`. Keyed by
// `aiProvider`. Held in a separate database from `link.keystore`
// because that database is strictly for `CryptoKey` material and
// has its own non-extractable / structured-clone constraints. We
// only store small string blobs here.

const DB_NAME = "link.local";
const STORE = "prefs";
const PREF_KEY = "aiProvider";
const VERSION = 1;

export type AiProviderId = "openai" | "anthropic" | "ollama";

export interface AiProviderConfig {
  providerId: AiProviderId;
  apiKey: string;
  model: string;
  /** Only meaningful for Ollama / OpenAI-compatible runners. */
  baseUrl: string | null;
}

/**
 * Default model suggestions per provider. The Settings panel uses
 * these to seed the model dropdown; the user can type any string.
 */
export const PROVIDER_MODELS: Record<AiProviderId, ReadonlyArray<string>> = {
  openai: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1"],
  anthropic: [
    "claude-3-5-haiku-latest",
    "claude-3-5-sonnet-latest",
    "claude-opus-4-1",
  ],
  ollama: ["llama3.1", "llama3.2", "qwen2.5", "mistral"],
};

export const PROVIDER_LABEL: Record<AiProviderId, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  ollama: "Ollama / OpenAI-compatible",
};

export const PROVIDER_DEFAULT_BASE_URL: Record<AiProviderId, string | null> = {
  openai: null,
  anthropic: null,
  ollama: "http://localhost:11434/v1",
};

/**
 * Open the small `prefs` database. Sibling to `link.keystore` —
 * deliberately separate so we never co-mingle CryptoKey material
 * with plain-text strings.
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
    if (idb === undefined) {
      reject(new Error("IndexedDB unavailable in this runtime"));
      return;
    }
    const req = idb.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb open failed"));
  });
}

export async function loadAiProvider(): Promise<AiProviderConfig | null> {
  const db = await openDb();
  try {
    return await new Promise<AiProviderConfig | null>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const req = store.get(PREF_KEY);
      req.onsuccess = () => {
        const value = req.result as AiProviderConfig | undefined;
        resolve(value ?? null);
      };
      req.onerror = () => reject(req.error ?? new Error("idb get failed"));
    });
  } finally {
    db.close();
  }
}

export async function saveAiProvider(
  config: AiProviderConfig,
): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      store.put(config, PREF_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("idb put failed"));
      tx.onabort = () => reject(tx.error ?? new Error("idb tx aborted"));
    });
  } finally {
    db.close();
  }
}

export async function clearAiProvider(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      store.delete(PREF_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("idb delete failed"));
      tx.onabort = () => reject(tx.error ?? new Error("idb tx aborted"));
    });
  } finally {
    db.close();
  }
}
